<template>
  <div class="flex h-full">
    <div id="search-tools" class="grow-0 flex-shink">
      <media-archive-search-box label="Zoek op trefwoord" v-model="query" />
      <media-archive-facets-available :facets="availableFacetsRenderable" />
    </div>

    <div class="column grow shrink basis-[60%]" id="search-results">
      <box-generic
        h-stretch
        w-stretch
        header="Archief"
        class="results-list-box h-full pl-[3vw]"
      >
        <div
          class="results-container flex flex-col w-full max-h-[calc(100vh_-_17rem)]"
        >
          <media-archive-facets-applied
            :items="appliedFacetsRenderable"
            :query="query"
          >
            <template #tags="{ item: tag }"> tag: {{ tag.title }} </template>
            <template #volumes="{ item: volume }">
              jaargang: {{ volume.volumeNumber }}, {{ volume.title }}
            </template>
            <template #seasons="{ item: season }">
              aflevering: nummer {{ season.issueNumber }}
            </template>
          </media-archive-facets-applied>

          <div class="relative grow flex flex-col pb-1 overflow-y-hidden">
            <div
              ref="scrollContainer"
              @scroll="onScroll"
              :class="[{ 'bg-white': fadeOut }]"
              class="scroll-container h-full overflow-x-hidden overflow-y-auto"
            >
              <slot :items="filteredItems" />
            </div>

            <transition-fade :duration="200">
              <div
                v-if="fadeOutTop"
                class="absolute inset-x-0 top-0 h-12 fade-out-top pointer-events-none"
              ></div>
            </transition-fade>
            <transition-fade :duration="200">
              <div
                v-if="fadeOutBottom"
                class="absolute inset-x-0 bottom-0 h-12 fade-out-bottom pointer-events-none"
              ></div>
            </transition-fade>
          </div>
        </div>
      </box-generic>
    </div>
  </div>
</template>

<script>
import { computed, reactive } from "vue";
import { mapState } from "vuex";

import BoxGeneric from "../BoxGeneric.vue";
import MediaArchiveFacetsApplied from "./MediaArchiveFacetsApplied.vue";
import MediaArchiveFacetsAvailable from "./MediaArchiveFacetsAvailable.vue";
import MediaArchiveSearchBox from "./MediaArchiveSearchBox.vue";
import TransitionFade from "../../magic/TransitionFade.vue";

import findBySlug from "../../../store/getters/resolve/find-by-slug";

export default {
  name: "MediaArchive",
  components: {
    BoxGeneric,
    MediaArchiveFacetsApplied,
    MediaArchiveFacetsAvailable,
    MediaArchiveSearchBox,
    TransitionFade,
  },
  props: {
    items: {
      type: Array,
      default: () => [],
    },
    fadeOut: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      scrollPosition: undefined,
      isScrollable: undefined,
    };
  },

  computed: {
    ...mapState(["container"]),

    // Single source of truth for types and query keys

    facetTypes() {
      // These facet types can be applied to the list of media archive items.
      // Each facet type is named by the media item property it filters by.
      return ["tags", "volumes", "seasons"];
    },

    queryKeys() {
      // Here we map the above defined facet types
      // onto localized URL query string keys.
      return {
        tags: "tag",
        volumes: "jaargang",
        seasons: "aflevering",
      };
    },

    // Normalizing and filtering items

    normalizedItems() {
      // Normalize items to make them searchable.
      // Implemented as a getter to leverage caching.
      // Normalization lower-cases all characters
      // and removes diacritics and accents.
      return this.items.map((item) => ({
        ...item,

        // Normalize title
        normalizedTitle: this.normalizeString(item.title),

        // Normalize cuePoints if the item has any (currently: songs only).
        ...(Object.prototype.hasOwnProperty.call(item, "cuePoints")
          ? {
              cuePoints: item.cuePoints.map((cuePoint) => ({
                ...cuePoint,

                // For each cuePoint, normalize its lyrics.
                normalizedLyrics: this.normalizeString(cuePoint.lyrics),
              })),
            }
          : {}),
      }));
    },

    filteredItems() {
      let items = this.normalizedItems;

      // Apply search query filters on title and,
      // if exists (currently not for video items), cue points.
      if (this.normalizedQuery) {
        items = items.filter(
          (item) =>
            this.matchesTitleSearch(item) || this.matchesCuePointsSearch(item),
        );
      }

      // Apply facet filters. For each of the facet types,
      // check if any facet item of that type has been applied,
      // and use the applied facet items to filter out all
      // non-matching media items.
      this.facetTypes.forEach((facetType) => {
        if (this.appliedFacetSlugs[facetType].length) {
          items = items.filter((item) => this.matchesFacet(facetType, item));
        }
      });

      return items;
    },

    // Handling the query string

    query: {
      // The query is taken straight from the URL.
      get() {
        return this.$route.query.zoek || "";
      },
      // When setting the query, it is set straight into the URL
      // by replacing the current route in-place.
      set(search) {
        this.$router.replace({
          ...this.$route,
          query: {
            // Facets remain untouched.
            ...this.$route.query,
            // But the search query is updated.
            zoek: search,
          },
        });
      },
    },

    normalizedQuery() {
      return this.normalizeString(this.query);
    },

    // Available facets

    availableFacetSlugs() {
      return reactive(
        Object.fromEntries(
          // For each facet type, create a map onto the available facet items.
          // Available facet items are such that are present in the current
          // filtered list of media items, but not currently applied as filters.
          this.facetTypes.map((type) => [
            type,
            computed(() => this.makeAvailableFacetSlugs(type)),
          ]),
        ),
      );
    },

    availableFacetsRenderable() {
      // For each facet type, collect the available facet items,
      // and prepare their data for rendering, by resolving
      // data needed to render the facet item label,
      // and creating a computed route object.
      return (
        this.facetTypes
          .map((type) => ({
            type,
            items: this.makeAvailableFacetRenderable(type),
          }))
          // Facet types without any facet items are left out.
          // Vue's list transition makes sure facet boxes
          // are nicely faded and moved out and in, depending
          // on which facet types have facet items available.
          .filter((facet) => facet.items.length)
      );
    },

    // Applied facets

    appliedFacetSlugs() {
      // For each facet type, get the applied facet slugs.
      // These applied facet slugs can be resolved
      // into applied facet items.
      return reactive(
        Object.fromEntries(
          this.facetTypes.map((type) => [
            type,
            computed(() => this.makeAppliedFacetSlugs(type)),
          ]),
        ),
      );
    },

    appliedFacets() {
      return reactive(
        Object.fromEntries(
          // For each facet type, traverse the applied
          // facet slug for that type and resolve
          // the corresponding facet item.
          this.facetTypes.map((type) => [
            type,
            computed(() => this.makeAppliedFacet(type)),
          ]),
        ),
      );
    },

    appliedFacetsRenderable() {
      // For each facet type, create renderables for its applied
      // facet items, by mapping data needed to render the label
      // and creating a computed route object. This route is used
      // to remove the given applied facet from the URL's query
      // string. The list is flattened, because all available
      // facets are rendered as one single list above the media.
      return this.facetTypes.flatMap((type) =>
        this.makeAppliedFacetRenderable(type),
      );
    },

    // Data sources for resolving slugs into facet items

    resolveSources() {
      // This computed getter maps each facet type to the
      // data source of corresponding items. This means
      // that the resulting array per type can be used
      // to find a facet item for any given facet slug.
      return {
        tags: this.container?.tags,
        volumes: this.container?.volumes,
        seasons: [].concat(
          ...this.container?.volumes?.map((volume) => volume.seasons),
        ),
      };
    },

    // Fade out top/bottom of scrollable list into white.
    fadeOutTop() {
      if (!this.fadeOut || this.scrollPosition === undefined) {
        return false;
      }
      return this.scrollPosition > 0;
    },
    fadeOutBottom() {
      if (
        !this.fadeOut ||
        this.scrollPosition === undefined ||
        !this.isScrollable
      ) {
        return false;
      }
      const scrollMax =
        this.$refs.scrollContainer?.scrollHeight -
        this.$refs.scrollContainer?.clientHeight;
      return (this.scrollPosition / scrollMax) * 100 < 100;
    },
  },

  watch: {
    filteredItems() {
      this.$nextTick(() => {
        this.checkIsScrollable();
        this.onScroll();
      });
    },
  },

  mounted() {
    this.$nextTick(() => {
      this.checkIsScrollable();
      this.onScroll();
    });
  },

  methods: {
    // String normalization

    normalizeString(s) {
      // Normalizing Unicode characters (e.g. ë -> e)
      // to prepare for search.
      return s
        .toLowerCase()
        .normalize("NFD")
        .replace(/[\u0300-\u036f]/g, "");
    },

    // Search string matching

    matchesTitleSearch(item) {
      // Checking a media item's normalized title
      // against the current normalized search string.
      return item.normalizedTitle.includes(this.normalizedQuery);
    },
    matchesCuePointsSearch(item) {
      // Checking a song's cue points' normalized lyrics lines
      // against the current normalized search string.
      return item.cuePoints?.some((cuePoint) =>
        cuePoint.normalizedLyrics.includes(this.normalizedQuery),
      );
    },

    // Facets matching

    matchesFacet(facetType, item) {
      // Checking a media item's facetable property
      // against the given facet type's applied facets.
      // The media item may only pass the test if all
      // (`Array.prototype.every()`) of the type's facet items
      // match one of the facetable propertys.
      return this.appliedFacetSlugs[facetType].every(
        // Before checking for a match, the facetable property
        // is mapped to its own slug property, because the
        // facetable properties are rendered as references.
        // E.g. each of a video's volumes is a reference
        // object. The object's only key, `slug`, can
        // be used to resolve the actual volume from the container.
        // To understand this better, have a look at the JSON
        // data rendered by the controller and loaded into the app.
        (slug) =>
          item[facetType].map((facetItem) => facetItem.slug).includes(slug),
      );
    },

    // Resolving slug references to target items.

    resolve(type, slug) {
      // Resolve a given facet slug into the corresponding fact item.
      // E.g. the volume slug 'jaargang-26-2020-2021' is resolved
      // to the corresponding container's volume object
      // having the same slug string. All facetable properties
      // are only such reference objects, containing merely a `slug` key.
      return findBySlug(this.resolveSources[type], slug);
    },

    // Available facets

    makeAvailableFacetSlugs(type) {
      // Collect unique facet type slugs of all items matching the currently applied filter.
      // `Set` is used to remove duplicates.
      // Each of the filtered media items gets all their reference slugs
      // for the given facet type extracted.
      // Then, slugs of already applied facets are removed.
      // The remaining facet slugs are 'available' to be applied,
      // reducing the set of filtered media items further.
      return [
        ...new Set(
          [].concat(...this.filteredItems.map((item) => item[type])).map(
            // Extract slug.
            (reference) => reference.slug,
          ),
        ),
      ].filter(
        // Exclude items that are already applied.
        (slug) => !this.appliedFacetSlugs[type].includes(slug),
      );
    },

    makeAvailableFacetRenderable(type) {
      // For the given facet type, traverse all available facet slugs,
      // resolve their related facet items, to obtain the data
      // necessary for rendering a facet item label.
      // Then, create a computed route used to add the facet slug
      // to the set of applied facets.
      return this.availableFacetSlugs[type].map(
        // Resolve slug into full facet item.
        (slug) => ({
          key: slug,
          item: this.resolve(type, slug),
          route: this.makeAvailableFacetRoute(type, slug),
        }),
      );
    },

    makeAvailableFacetRoute(type, slug) {
      // Make a route for adding a specific facet item to the current set of filters.
      return computed(() => ({
        // Keep route
        ...this.$route,
        // Modify query
        query: {
          // Re-use query (if any)
          ...(this.$route.query ?? {}),
          // But adding a new slug to the given facet type.
          // E.g. for the "jaargang 26: 2020-2021" volume facet item,
          // this adds 'jaargang=jaargang-26-2020-2021'
          // to the query string of the new route.
          [this.queryKeys[type]]: [...this.appliedFacetSlugs[type], slug],
        },
      }));
    },

    // Applied facets

    makeAppliedFacetSlugs(type) {
      // For the given facet type, extract all facet slugs from the
      // current route URL's query string.

      // A single route query item is a string, while multiple are an array.
      // Let's normalize that.
      const query = this.$route.query[this.queryKeys[type]] ?? [];

      // Remove duplicates.
      return [
        ...new Set(
          // Ensure array. (Might be string if only one item.)
          Array.isArray(query) ? query : [query],
        ),
      ].filter(
        // Remove empty items.
        (slug) => !!slug.length,
      );
    },

    makeAppliedFacet(type) {
      // For the given facet type, resolve all applied facet slugs
      // to the corresponding facet items.
      //
      // E.g. for the query segment 'aflevering=1e-kwartaal',
      // `type` would be 'seasons', and `slug` would be '1e-kwartaal'.
      // This slug would be resolved to the first season
      // found in all of the container's volumes' seasons.
      //
      // Note that anyway *any* first season of a volume would be
      // matched in the filtering stage, because filtering is done
      // based on the *reference slug* only! The resolved data
      // is merely used to render the facet label.
      // In this case, the season's `issueNumber`.
      return this.appliedFacetSlugs[type].map(
        // Resolve slug into full facet item.
        // E.g. if 'type' is 'volumes',
        // then resolve the given volume slug
        // into a volume object.
        (slug) => this.resolve(type, slug),
      );
    },

    makeAppliedFacetRenderable(type) {
      // For the given facet type, prepare all applied facets for rendering.
      // A route is created
      return this.appliedFacets[type].map((facetItem) => ({
        // Prepare a single applied facet item for rendering.
        type,
        item: facetItem,
        key: facetItem.slug,
        route: this.makeAppliedFacetRoute(type, facetItem),
      }));
    },

    // Make a route for removing a specific facet item from the current set of filters.
    makeAppliedFacetRoute(type, facetItem) {
      return computed(() => ({
        // Keep route
        ...this.$route,
        // Modify query
        query: {
          // Re-use query
          ...this.$route.query,
          // But modify the given facet type,
          // removing the slug responsible for the facet item.
          // E.g. for the "x tag: pasen" tag, this removes
          // 'tag=pasen' from the query string of the new route.
          [this.queryKeys[type]]: this.appliedFacetSlugs[type].filter(
            (slug) => facetItem.slug !== slug,
          ),
        },
      }));
    },

    checkIsScrollable() {
      this.isScrollable = this.$refs.scrollContainer
        ? this.$refs.scrollContainer.scrollHeight >
          this.$refs.scrollContainer.clientHeight
        : undefined;
    },
    onScroll() {
      this.scrollPosition = this.$refs.scrollContainer
        ? Math.ceil(this.$refs.scrollContainer.scrollTop)
        : undefined;
    },
  },
};
</script>

<style scoped lang="css">
/* Search tools (left column) */

#search-tools {
  @apply basis-[30%];
}

/* Facets */

::v-deep(.facet-link) {
  @apply border border-solid rounded-[6px] text-orange-500 inline-block font-light tracking-[.5px] mr-[.4vw] mt-[1vh] py-px px-[9px] no-underline lowercase scale-100 duration-300 transition-all whitespace-nowrap hover:text-[#f65800] hover:scale-110;
  /* Note `#f65800`: saturate($color-orange, 20%); */
}

.fade-out-top {
  background-image: linear-gradient(
    to top,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.75) 40%,
    rgba(255, 255, 255, 0.9) 60%,
    white
  );
}

.fade-out-bottom {
  background-image: linear-gradient(
    to bottom,
    rgba(255, 255, 255, 0),
    rgba(255, 255, 255, 0.75) 40%,
    rgba(255, 255, 255, 0.9) 60%,
    white
  );
}
</style>
